import React, { useEffect, useState } from 'react';
import api from 'api/api';
import auth from 'utils/auth';
import Loader from 'ui/Loader/Loader';
import NoBeneficiary from 'components/modules/Beneficiaries/NoBeneficiary/NoBeneficiary';
import BeneficiaryTable from 'components/modules/Beneficiaries/BeneficiaryTable/BeneficiaryTable';
import BeneficiaryRow from 'components/modules/Beneficiaries/BeneficiaryTable/BeneficiaryRow/BeneficiaryRow';
import BeneficiaryCol from 'components/modules/Beneficiaries/BeneficiaryTable/BeneficiaryCol/BeneficiaryCol';
import { INetwork } from 'components/modules/Beneficiaries/Beneficiaries.model';
import { IAPIBeneficiaryMobileBulk, IMobileBulkIds, ITransformedModileBulkId } from './BulkMobileBen.model';
import EditMobileBulkBen from './EditMobileBulkBen';

enum States {
  browsing = 'browsing',
  editing = 'editing',
}

interface ISingleMobileBen {
  networks: INetwork[];
}

function transformMobileBulkIds(bulkIds: IMobileBulkIds) {
  const keys = Object.keys(bulkIds);

  const result = keys.map((key) => {
    const item = bulkIds[key];
    return {
      id: key,
      alias: item.alias,
      count: item.count,
      beneficiaries: item.beneficiaries,
    };
  });

  return result;
}

const BulkMobileBen = ({ networks }: ISingleMobileBen) => {
  const [loadingData, setLoadingData] = useState(false);
  const [data, setData] = useState<ITransformedModileBulkId[]>([]);
  const [editData, setEditData] = useState<ITransformedModileBulkId | null>(null);

  const [state, setState] = useState<States>(States.browsing);

  const fetchBeneficiaries = () => {
    setLoadingData(true);
    api
      .get(`/beneficiary/mobilemoney/bulk?loginId=${auth.data?.loginId || ''}`)
      .then((response: IAPIBeneficiaryMobileBulk) => {
        const transformedBulkList: ITransformedModileBulkId[] = transformMobileBulkIds(response.bulkIds);
        setData(transformedBulkList);
      })
      .catch((error) => {
        console.error(error);
        setData([]);
      })
      .finally(() => {
        setLoadingData(false);
      });
  };

  useEffect(() => {
    fetchBeneficiaries();
  }, []);

  const openEditForm = (e: React.MouseEvent<HTMLDivElement>) => {
    setState(States.editing);
    const object = data.find(({ id }) => id === e.currentTarget.id);
    if (object) {
      setEditData(object);
    }
  };

  const closeEditForm = () => {
    setState(States.browsing);
    fetchBeneficiaries();
  };

  if (state === States.editing && editData) {
    return <EditMobileBulkBen data={editData} networks={networks} onClose={closeEditForm} />;
  }

  const renderTable = () => (
    <BeneficiaryTable>
      {data.map((bulkBeneficiary) => (
        <BeneficiaryRow onClick={openEditForm} id={String(bulkBeneficiary.id)} key={bulkBeneficiary.id}>
          <BeneficiaryCol label="Bulk ID" value={bulkBeneficiary.id} width="20%" minWidth="240px" />
          <BeneficiaryCol
            label="Total Beneficiaries"
            value={String(bulkBeneficiary.count)}
            width="80%"
            minWidth="300px"
          />
        </BeneficiaryRow>
      ))}
    </BeneficiaryTable>
  );

  return (
    <>
      {loadingData && <Loader fullHeight />}
      {!loadingData && data.length === 0 && <NoBeneficiary />}
      {!loadingData && data.length > 0 && renderTable()}
    </>
  );
};

export default BulkMobileBen;
